"use client";

import { useState } from "react";
import Link from "next/link";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsAppButton";
import { PhoneIcon, MessageCircleIcon, MapPinIcon, ClockIcon } from "../components/Icons";

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    course: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const text = `Hi Future Institute,%0A%0AName: ${formData.name}%0AEmail: ${formData.email}%0APhone: ${formData.phone}%0ACourse Interest: ${formData.course}%0AMessage: ${formData.message}`;
    window.open(`https://wa.me/917902222553?text=${text}`, "_blank");
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
  };

  return (
    <>
      <Header />

      {/* Page Hero */}
      <section className="page-hero" id="contact-hero">
        <h1>Get in Touch</h1>
        <p>
          Have questions about admissions? We&apos;re here to help. Reach out to us
          via phone, WhatsApp, or visit our center.
        </p>
        <div className="page-hero-breadcrumb">
          <Link href="/">Home</Link>
          <span>/</span>
          <span>Contact</span>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="section" id="contact-info">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-info-card" id="contact-card-phone">
              <div className="contact-info-icon"><PhoneIcon size={24} /></div>
              <h3>Call Us</h3>
              <p>
                <a href="tel:+917902222552" style={{ color: "var(--primary)" }}>
                  +91 79022 22552
                </a>
                <br />
                <a href="tel:+917902222553" style={{ color: "var(--primary)" }}>
                  +91 79022 22553
                </a>
              </p>
            </div>

            <div className="contact-info-card" id="contact-card-whatsapp">
              <div className="contact-info-icon"><MessageCircleIcon size={24} /></div>
              <h3>WhatsApp</h3>
              <p>
                <a
                  href="https://wa.me/917902222553"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#8B1A1A", fontWeight: 600 }}
                >
                  +91 79022 22553
                </a>
                <br />
                <small style={{ color: "var(--gray-400)" }}>
                  Chat with us instantly
                </small>
              </p>
            </div>

            <div className="contact-info-card" id="contact-card-address">
              <div className="contact-info-icon"><MapPinIcon size={24} /></div>
              <h3>Visit Us</h3>
              <p>
                Near Malayala Manorama Office,
                <br />
                Opp. Central Residency Hotel,
                <br />
                Thampanoor, Trivandrum - 695001
              </p>
            </div>

            <div className="contact-info-card" id="contact-card-hours">
              <div className="contact-info-icon"><ClockIcon size={24} /></div>
              <h3>Office Hours</h3>
              <p>
                Mon - Sat: 9:00 AM - 6:00 PM
                <br />
                Sunday: Closed
                <br />
                <small style={{ color: "var(--gray-400)" }}>
                  WhatsApp available 24/7
                </small>
              </p>
            </div>
          </div>

          {/* More Phone Numbers */}
          <div
            style={{
              textAlign: "center",
              padding: "1.5rem",
              background: "var(--cream)",
              borderRadius: "16px",
              marginBottom: "4rem",
            }}
          >
            <p style={{ color: "var(--gray-600)", fontSize: "0.95rem" }}>
              <strong>Additional Contact Numbers:</strong>{" "}
              <a href="tel:+919142222552" style={{ color: "var(--primary)" }}>
                +91 91422 22552
              </a>{" "}
              |{" "}
              <a href="tel:+917902322552" style={{ color: "var(--primary)" }}>
                +91 79023 22552
              </a>
            </p>
          </div>

          {/* Form & Map */}
          <div className="contact-form-section">
            <div className="contact-form" id="contact-form">
              <h3>Send Us a Message</h3>
              {submitted && (
                <div
                  style={{
                    padding: "1rem",
                    background: "rgba(139, 26, 26, 0.1)",
                    border: "1px solid rgba(139, 26, 26, 0.3)",
                    borderRadius: "12px",
                    color: "#8B1A1A",
                    fontWeight: 600,
                    marginBottom: "1.5rem",
                    textAlign: "center",
                  }}
                >
                  ✅ Redirecting to WhatsApp...
                </div>
              )}
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    placeholder="Enter your full name"
                    required
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    placeholder="Enter your phone number"
                    required
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="course">Course Interest</label>
                  <select
                    id="course"
                    value={formData.course}
                    onChange={(e) =>
                      setFormData({ ...formData, course: e.target.value })
                    }
                  >
                    <option value="">Select a programme</option>
                    <optgroup label="UG Programmes">
                      <option>BA</option>
                      <option>B.Com</option>
                      <option>B.Sc</option>
                      <option>BCA</option>
                      <option>BBA</option>
                      <option>BLiSc</option>
                    </optgroup>
                    <optgroup label="PG Programmes">
                      <option>MA</option>
                      <option>M.Com</option>
                      <option>M.Sc</option>
                      <option>M.L.I.Sc</option>
                    </optgroup>
                    <optgroup label="Professional">
                      <option>MBA</option>
                    </optgroup>
                    <optgroup label="Others">
                      <option>Diploma</option>
                      <option>Certificate Course</option>
                      <option>Other</option>
                    </optgroup>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    placeholder="Tell us about your requirements..."
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-whatsapp form-submit"
                  id="contact-submit-btn"
                  style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}
                >
                  <MessageCircleIcon size={18} style={{ strokeWidth: 2.5 }} /> Send via WhatsApp
                </button>
              </form>
            </div>

            <div className="contact-map" id="contact-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d986.5204084996863!2d76.95271179912226!3d8.49144378191802!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b05bb384ca08b01%3A0xaeb40cb83f2d275d!2sAuthorised%20Distance%20education%20centre%20of%20madurai%20kamaraj%20%26%20Madras%20university!5e0!3m2!1sen!2sin!4v1782228341264!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Future Institute Location"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-banner" id="contact-cta">
        <h2>Prefer a Quick Chat?</h2>
        <p>
          Skip the form and connect with us directly on WhatsApp for instant
          assistance with admissions.
        </p>
        <div className="cta-buttons">
          <a
            href="https://wa.me/917902222553?text=Hi%20Future%20Institute%2C%20I%20need%20assistance%20with%20admissions."
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-whatsapp"
            id="contact-cta-whatsapp"
            style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
          >
            <MessageCircleIcon size={18} style={{ strokeWidth: 2.5 }} /> Chat on WhatsApp Now
          </a>
          <a
            href="tel:+917902222553"
            className="btn btn-outline"
            id="contact-cta-call"
            style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
          >
            <PhoneIcon size={18} style={{ strokeWidth: 2.5 }} /> Call: +91 79022 22553
          </a>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </>
  );
}
